import React, { useMemo, forwardRef, useImperativeHandle } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { OvertimeRequest } from "@/types";
import { OvertimeStatus, TimeRange } from "@/types/enums";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
;
import { cn } from "@/lib/utils";
import ExportButton from "@/components/ui/ExportButton";
import InteractiveBarChart from "@/components/ui/InteractiveBarChart";
import { ChartConfig } from "@/components/ui/chart";
import { TooltipProps } from "recharts";

interface DashboardFilters {
  timeRange: string;
  departments: string[];
  statuses: string[];
}

interface DepartmentTrendlineChartProps {
  requests: OvertimeRequest[];
  title?: string;
  isLoading?: boolean;
  standalone?: boolean;
  filters?: DashboardFilters; // Make filters optional
  startDate?: Date; // Optional override for date range
  endDate?: Date; // Optional override for date range
}

interface DepartmentTrendlineChartHandle {
  download: () => void;
}

// Custom legend component for department trend chart
const CustomDepartmentLegend = ({ payload }: { payload?: Array<{ value: string; color: string }> }) => {
  if (!payload?.length) {
    return null;
  }

  // Sort by value (count) and take top 10
  const sortedPayload = payload
    .filter(item => typeof item.value === 'number' && item.value > 0)
    .sort((a, b) => (b.value as unknown as number) - (a.value as unknown as number));
  
  const top10 = sortedPayload.slice(0, 10);
  const remainingCount = sortedPayload.length - 10;

  return (
    <div className="flex flex-wrap items-center justify-center gap-1 px-1 py-1 max-w-full">
      {top10.map((item) => {
        return (
          <div
            key={item.value}
            className="flex items-center gap-1 text-[10px]"
          >
            <div
              className="h-1.5 w-1.5 shrink-0 rounded-[1px]"
              style={{ backgroundColor: item.color }}
            />
            <span className="text-muted-foreground truncate max-w-[60px]" title={item.value}>
              {String(item.value).length > 8 ? String(item.value).substring(0, 8) + '...' : item.value}
            </span>
          </div>
        );
      })}
      {remainingCount > 0 && (
        <div className="flex items-center gap-1 text-[10px] text-muted-foreground">
          <div className="h-1.5 w-1.5 shrink-0 rounded-[1px] bg-muted-foreground/30" />
          <span>+{remainingCount}</span>
        </div>
      )}
    </div>
  );
};

// Custom tooltip component for department trend chart
const CustomDepartmentTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (!active || !payload || payload.length === 0) {
    return null;
  }

  // Filter out departments with value <= 0 and sort by value (descending)
  const validPayloads = payload
    .filter(item => typeof item.value === 'number' && item.value > 0)
    .sort((a, b) => (b.value as number) - (a.value as number));

  if (validPayloads.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-background p-3 shadow-lg z-[60] min-w-[200px]">
      <div className="font-semibold text-sm mb-2 text-center border-b pb-1">
        {label}
      </div>
      <div className="space-y-1">
        {validPayloads.map((item, index) => {
          // Get color from the item's payload or use a fallback
          const color = item.payload?.fill || item.color || `hsl(var(--chart-${(index % 20) + 1}))`;
          return (
            <div key={item.dataKey} className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <div 
                  className="w-3 h-3 rounded-full flex-shrink-0 border border-border/20" 
                  style={{ backgroundColor: color }}
                />
                <span className="text-sm text-left truncate font-medium">
                  {item.name}
                </span>
              </div>
              <span className="text-sm font-mono font-semibold text-right flex-shrink-0 text-foreground">
                {typeof item.value === 'number' ? item.value.toLocaleString() : '0'}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};


const DepartmentTrendlineChart = forwardRef<DepartmentTrendlineChartHandle, DepartmentTrendlineChartProps>(
  function DepartmentTrendlineChart(
    {
      requests,
      title = "Department Overtime Distribution",
      isLoading = false,
      standalone = false,
      filters,
      startDate: propStartDate,
      endDate: propEndDate
    },
    ref
  ) {
  // Always use the passed filters (no internal state)
  const effectiveTimeRange = filters?.timeRange || TimeRange.THIRTY_DAYS;

  // Process data for the chart
  const { chartData, departments, chartConfig, hasData } = useMemo(() => {
    // Check if we have valid requests data
    if (!requests || requests.length === 0) {
      return {
        chartData: [],
        departments: [],
        chartConfig: {} as ChartConfig,
        hasData: false
      };
    }
    
    // Only include approved requests
    const approvedRequests = requests.filter(req => req.status === OvertimeStatus.APPROVED);
    
    if (approvedRequests.length === 0) {
      return {
        chartData: [],
        departments: [],
        chartConfig: {} as ChartConfig,
        hasData: false
      };
    }
    
    // Get all unique departments
    const uniqueDepartments = Array.from(new Set(approvedRequests.map(req => 
      req.department ? req.department : 'Unassigned'
    )));

    // Create chart config for departments
    const config: ChartConfig = {
      hours: {
        label: "Hours",
      },
    };

    // Add departments to config with colors - ensure no repetition
    const chartColors = [
      "hsl(var(--chart-1))",
      "hsl(var(--chart-2))", 
      "hsl(var(--chart-3))",
      "hsl(var(--chart-4))",
      "hsl(var(--chart-5))",
      "hsl(var(--chart-6))",
      "hsl(var(--chart-7))",
      "hsl(var(--chart-8))",
      "hsl(var(--chart-9))",
      "hsl(var(--chart-10))",
      "hsl(var(--chart-11))",
      "hsl(var(--chart-12))",
      "hsl(var(--chart-13))",
      "hsl(var(--chart-14))",
      "hsl(var(--chart-15))",
      "hsl(var(--chart-16))",
      "hsl(var(--chart-17))",
      "hsl(var(--chart-18))",
      "hsl(var(--chart-19))",
      "hsl(var(--chart-20))",
    ];

    // Sort departments alphabetically for consistent color assignment
    const sortedDepartments = [...uniqueDepartments].sort();
    
    sortedDepartments.forEach((dept, index) => {
      config[dept] = {
        label: dept,
        color: chartColors[index % chartColors.length],
      };
    });

    // Calculate date range based on effectiveTimeRange filter or use provided dates
    const today = new Date();
    let startDate = new Date(today);
    let endDate = new Date(today);
    
    // If startDate and endDate props are provided, use them instead of calculating from timeRange
    if (propStartDate && propEndDate) {
      startDate = new Date(propStartDate);
      endDate = new Date(propEndDate);
    } else {
      // Handle dashboard filter values or component's own time range
      if (effectiveTimeRange === TimeRange.ALL) {
      // For "all", dynamically find the earliest date from actual data
      if (approvedRequests.length > 0) {
        // Filter out requests with invalid dates before calculating minimum
        const validDates = approvedRequests
          .filter(req => req.date && !isNaN(new Date(req.date).getTime()))
          .map(req => new Date(req.date).getTime());
        
        if (validDates.length > 0) {
          const earliestDate = new Date(Math.min(...validDates));
          startDate.setTime(earliestDate.getTime());
        } else {
          // Fallback to a reasonable default if no valid dates
          startDate.setFullYear(today.getFullYear() - 1, 0, 1); // 1 year ago
        }
      } else {
        // Fallback to a reasonable default if no data
        startDate.setFullYear(today.getFullYear() - 1, 0, 1); // 1 year ago
      }
    } else if (effectiveTimeRange === TimeRange.THREE_SIXTY_FIVE_DAYS || effectiveTimeRange === TimeRange.ONE_YEAR) {
      // Last year - January 1st of last year to December 31st of last year
      startDate.setFullYear(today.getFullYear() - 1, 0, 1);
      endDate.setFullYear(today.getFullYear() - 1, 11, 31);
    } else if (effectiveTimeRange === TimeRange.CALENDAR_MONTH) {
      // Current calendar month (1st to last day)
      startDate.setDate(1);
      endDate.setDate(endDate.getDate()); // Keep end date as today
    } else if (effectiveTimeRange === TimeRange.LAST_CALENDAR_MONTH) {
      // Previous calendar month - Set start to 1st day and end to last day of previous month
      startDate.setMonth(startDate.getMonth() - 1, 1);
      endDate.setMonth(endDate.getMonth(), 0); // Set to last day of previous month
    } else {
      // Handle numeric time ranges (7d, 30d, 90d)
      const days = parseInt(effectiveTimeRange.replace('d', ''));
      if (!isNaN(days)) {
        startDate.setDate(startDate.getDate() - days + 1);
      } else {
        startDate.setDate(startDate.getDate() - 90); // Default to 90 days
      }
    }
    }
    
    // Ensure dates are properly bounded and valid
    if (startDate > endDate) {
      // If start date is after end date, swap them
      [startDate, endDate] = [endDate, startDate];
    }
    
    // Ensure we don't go beyond today for future dates
    if (endDate > today) {
      endDate.setTime(today.getTime());
    }
    
    // Ensure start date is not before a reasonable limit (e.g., 5 years ago)
    const minDate = new Date(today.getFullYear() - 5, 0, 1);
    if (startDate < minDate) {
      startDate.setTime(minDate.getTime());
    }
    
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(23, 59, 59, 999);

    // Group data by date or month depending on range
    const dataByDate: Record<string, Record<string, number>> = {};
    
    // Filter requests by date range - use the actual overtime date, not submission date
    const filteredRequests = approvedRequests.filter(req => {
      // Always use req.date as it represents the actual overtime date
      if (!req.date) return false;
      
      const date = new Date(req.date);
      // Check if the date is valid
      if (isNaN(date.getTime())) return false;
      
      return date >= startDate && date <= endDate;
    });

    
    // Process filtered requests
    filteredRequests.forEach(req => {
      const date = new Date(req.date);
      // For very long ranges (all, 365d, 1y), aggregate monthly to keep dataset small
      const isLongRange = effectiveTimeRange === TimeRange.ALL || effectiveTimeRange === TimeRange.THREE_SIXTY_FIVE_DAYS || effectiveTimeRange === TimeRange.ONE_YEAR;
      const dateKey = isLongRange
        ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
        : date.toISOString().split('T')[0];
      const department = req.department || 'Unassigned';
      const hours = typeof req.hours === 'number' ? req.hours : 0;
      
      if (!dataByDate[dateKey]) {
        dataByDate[dateKey] = {};
        uniqueDepartments.forEach(dept => {
          dataByDate[dateKey][dept] = 0;
        });
      }
      
      dataByDate[dateKey][department] = (dataByDate[dateKey][department] || 0) + hours;
    });

    // Generate complete range with all dates/months - ONLY within the selected range
    const completeData: Array<Record<string, string | number>> = [];
    const isLongRange = effectiveTimeRange === TimeRange.ALL || effectiveTimeRange === TimeRange.THREE_SIXTY_FIVE_DAYS || effectiveTimeRange === TimeRange.ONE_YEAR;
    const isCalendarMonth = effectiveTimeRange === TimeRange.CALENDAR_MONTH || effectiveTimeRange === TimeRange.LAST_CALENDAR_MONTH;
    
    if (isLongRange) {
      // Monthly buckets from start to end - ensure ALL months are included
      const iter = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
      const endMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 1);
      
      while (iter <= endMonth) {
        const key = `${iter.getFullYear()}-${String(iter.getMonth() + 1).padStart(2, '0')}`;
        const row: Record<string, string | number> = { date: key };
        
        // Initialize all departments with 0 hours for this month
        uniqueDepartments.forEach(dept => {
          row[dept] = dataByDate[key]?.[dept] || 0;
        });
        
        completeData.push(row);
        iter.setMonth(iter.getMonth() + 1);
      }
    } else if (isCalendarMonth) {
      // For calendar months, only show dates within the selected month
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const row: Record<string, string | number> = { date: dateStr };
        
        // Initialize all departments with 0 hours for this date
        uniqueDepartments.forEach(dept => {
          row[dept] = dataByDate[dateStr]?.[dept] || 0;
        });
        
        completeData.push(row);
        currentDate.setDate(currentDate.getDate() + 1);
      }
    } else {
      // Daily buckets for other ranges
      const currentDate = new Date(startDate);
      while (currentDate <= endDate) {
        const dateStr = currentDate.toISOString().split('T')[0];
        const row: Record<string, string | number> = { date: dateStr };
        
        // Initialize all departments with 0 hours for this date
        uniqueDepartments.forEach(dept => {
          row[dept] = dataByDate[dateStr]?.[dept] || 0;
        });
        
        completeData.push(row);
        currentDate.setDate(currentDate.getDate() + 1);
      }
    }

    // Filter out empty date entries to prevent showing unnecessary empty dates
    const filteredCompleteData = completeData.filter(row => {
      // Check if any department has data for this date
      return uniqueDepartments.some(dept => {
        const value = row[dept];
        return typeof value === 'number' && value > 0;
      });
    });

    return {
      chartData: filteredCompleteData.length > 0 ? filteredCompleteData : completeData, // Fallback to complete data if filtered is empty
      departments: uniqueDepartments,
      chartConfig: config,
      hasData: filteredRequests.length > 0
    };
  }, [requests, effectiveTimeRange, propStartDate, propEndDate]);

  const handleDownload = () => {
    try {
      // Create CSV content
      const headers = ['Date', ...departments];
      const csvContent = [
        headers.join(','),
        ...chartData.map(item => {
          const deptValues = departments.map(dept => typeof item[dept] === 'number' ? item[dept] : 0);
          return [item.date, ...deptValues].join(',');
        })
      ].join('\n');
      
      // Create blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `department_trends_${effectiveTimeRange}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success('Data downloaded successfully');
    } catch {
      
      toast.error('Failed to download data');
    }
  };

  // Find the department with the highest growth
  const topDepartment = useMemo(() => {
    if (!hasData || departments.length === 0 || chartData.length < 2) return null;
    
    const growthByDept = departments.map(dept => {
      const firstValue = typeof chartData[0]?.[dept] === 'number' ? chartData[0][dept] as number : 0;
      const lastValue = typeof chartData[chartData.length - 1]?.[dept] === 'number' ? chartData[chartData.length - 1][dept] as number : 0;
      return {
        department: dept,
        growth: lastValue - firstValue
      };
    });
    
    return growthByDept.sort((a, b) => b.growth - a.growth)[0];
  }, [chartData, departments, hasData]);

  useImperativeHandle(ref, () => ({
    download: handleDownload
  }));

  // Chart content that will be used in both standalone and card modes
  const chartContent = (
    <div className={cn("h-80", isLoading ? "opacity-50" : "")}>
      <InteractiveBarChart
        data={chartData}
        config={chartConfig}
        dataKeys={departments}
        isLoading={isLoading}
        emptyStateMessage="No overtime trend data available"
        emptyStateSubMessage="Submit and approve overtime requests to see department trends"
        stacked={true}
        hideLabel={false}
        customTooltip={<CustomDepartmentTooltip />}
        customLegend={<CustomDepartmentLegend />}
      />
    </div>
  );

  // Render with or without Card container based on standalone prop
  if (standalone) {
    return (
      <div className="space-y-4">
        <div className="flex flex-row items-center justify-between gap-4">
          <h3 className="text-xl font-bold text-left">{title}</h3>
          <ExportButton onClick={handleDownload} disabled={!hasData || isLoading} />
        </div>
        {chartContent}
        {topDepartment && topDepartment.growth > 0 && (
          <div className="flex-col items-start gap-2 text-sm">
            <div className="flex gap-2 font-medium leading-none">
              {topDepartment.department} shows highest growth trend
              <TrendingUp className="h-4 w-4" />
            </div>
            <div className="leading-none text-muted-foreground">
              Showing overtime trends across {departments.length} departments for {effectiveTimeRange}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="shadow-md hover:shadow-lg transition-shadow duration-200">
      <CardHeader className="flex flex-row items-center justify-between gap-4 space-y-0 border-b py-4 px-6">
        <CardTitle className="text-xl font-bold text-left">
          {title}
        </CardTitle>
        <div className="flex items-center gap-2 ml-auto">
          <ExportButton onClick={handleDownload} disabled={!hasData || isLoading} />
        </div>
      </CardHeader>
      <CardContent className="px-2 pt-4 sm:px-6 sm:pt-6 pb-2">
        {chartContent}
      </CardContent>
      {topDepartment && topDepartment.growth > 0 && (
        <CardFooter className="flex-col items-start gap-2 text-sm pt-4">
          <div className="flex gap-2 font-medium leading-none">
            {topDepartment.department} shows highest growth trend
            <TrendingUp className="h-4 w-4" />
          </div>
          <div className="leading-none text-muted-foreground">
            Showing overtime trends across {departments.length} departments for {effectiveTimeRange}
          </div>
        </CardFooter>
      )}
    </Card>
  );
});

export default DepartmentTrendlineChart;
